import {
  formatMessageListDisplay,
  messageListCollapsedSummary,
  messageListExpandedRows,
  normalizeMessageList,
} from "./importMessagesDisplay";

const ELEVATION_COUNT_PATTERN = /^(\d+)\s+record\s+con\s+(?:errore|warning)\s*:/i;

const sumElevationCounts = (messages) => (
  (messages || []).reduce((total, message) => {
    const match = ELEVATION_COUNT_PATTERN.exec(String(message || "").trim());
    return match ? total + Number(match[1]) : total;
  }, 0)
);

const rowProblemBucketHasItems = (entry, bucket) => {
  if (!entry || typeof entry !== "object") {
    return false;
  }
  const items = entry[bucket];
  if (Array.isArray(items)) {
    return items.length > 0;
  }
  if (items && typeof items === "object") {
    return Object.keys(items).length > 0;
  }
  return false;
};

const resolveRowProblemsForCount = (importData) => {
  if (!importData) {
    return {};
  }
  const explicit = importData.row_problems;
  if (explicit && typeof explicit === "object" && Object.keys(explicit).length > 0) {
    return explicit;
  }
  const legacyErrors = importData.row_errors || {};
  const legacyWarnings = importData.row_warnings || {};
  const merged = {};
  const keys = new Set([...Object.keys(legacyErrors), ...Object.keys(legacyWarnings)]);
  keys.forEach((key) => {
    const entry = {};
    if (legacyErrors[key]) {
      entry.errors = legacyErrors[key];
    }
    if (legacyWarnings[key]) {
      entry.warnings = legacyWarnings[key];
    }
    if (entry.errors || entry.warnings) {
      merged[key] = entry;
    }
  });
  return merged;
};

const hasStructuredRowProblems = (importData) => (
  Boolean(importData)
  && (
    Object.prototype.hasOwnProperty.call(importData, "row_problems")
    || importData.row_errors
    || importData.row_warnings
  )
);

export const countImportRowLevelErrors = (importData) => {
  if (!importData) {
    return 0;
  }
  if (hasStructuredRowProblems(importData)) {
    return Object.values(resolveRowProblemsForCount(importData))
      .filter((entry) => rowProblemBucketHasItems(entry, "errors"))
      .length;
  }
  const total = Number(importData.products_with_errors || 0);
  const elevated = sumElevationCounts(normalizeMessageList(importData.global_errors || []));
  return Math.max(0, total - elevated);
};

export const countImportRowLevelWarnings = (importData) => {
  if (!importData) {
    return 0;
  }
  if (hasStructuredRowProblems(importData)) {
    return Object.values(resolveRowProblemsForCount(importData))
      .filter((entry) => rowProblemBucketHasItems(entry, "warnings"))
      .length;
  }
  const total = Number(importData.products_with_warnings || 0);
  const elevated = sumElevationCounts(normalizeMessageList(importData.global_warnings || []));
  return Math.max(0, total - elevated);
};

const buildImportRowCountSummary = (count, kind) => {
  if (count <= 0) {
    return [];
  }
  const recordLabel = count === 1 ? "1 record" : `${count.toLocaleString("it-IT")} record`;
  const label = kind === "warning" ? "warning di import" : "errori di import";
  return [`Sono presenti ${recordLabel} con ${label}.`];
};

export const buildImportRowErrorsSummary = (importData) => (
  buildImportRowCountSummary(countImportRowLevelErrors(importData), "error")
);

export const buildImportRowWarningsSummary = (importData) => (
  buildImportRowCountSummary(countImportRowLevelWarnings(importData), "warning")
);

export const normalizeGlobalErrors = (importData) => {
  const errors = importData?.global_errors;
  if (!Array.isArray(errors)) {
    return [];
  }
  return normalizeMessageList(errors);
};

export const normalizeGlobalWarnings = (importData) => {
  const warnings = importData?.global_warnings;
  if (!Array.isArray(warnings)) {
    return [];
  }
  return normalizeMessageList(warnings);
};

export const formatImportTypeLabel = (importType) => {
  if (importType === "scheduled") {
    return "Schedulato";
  }
  if (importType === "manual_all") {
    return "Manuale totale (Importa tutti)";
  }
  if (importType === "manual_selected") {
    return "Manuale selezionati (full)";
  }
  if (importType === "manual_single") {
    return "Manuale singolo full";
  }
  if (importType === "manual_stored") {
    return "Manuale singolo (feed normalizzato)";
  }
  if (importType === "manual") {
    return "Manuale (legacy)";
  }
  return importType ? String(importType) : "—";
};

export const formatRunTriggerLabel = (summary) => formatImportTypeLabel(
  summary?.trigger || summary?.import_type,
);

export const buildCombinedGlobalErrors = (importData) => (
  normalizeGlobalErrors(importData)
);

export const formatGlobalErrorsDisplay = (importData) => {
  const errors = buildCombinedGlobalErrors(importData);
  if (!errors.length) {
    return "";
  }

  const lines = [];
  const importType = importData?.trigger || importData?.import_type;
  const productsImported = Number(importData?.products_imported || 0);
  const productsWithErrors = Number(importData?.products_with_errors || 0);

  if (importType) {
    lines.push(`Tipo import: ${formatImportTypeLabel(importType)}`);
  }
  lines.push(`Prodotti importati: ${productsImported.toLocaleString("it-IT")}`);
  lines.push(`Prodotti con errori: ${productsWithErrors.toLocaleString("it-IT")}`);
  lines.push("");
  return `${lines.join("\n")}\n${formatMessageListDisplay(errors)}`.trimEnd();
};

export const globalErrorsCollapsedSummary = (importData) => (
  messageListCollapsedSummary(buildCombinedGlobalErrors(importData), {
    emptyLabel: "Nessun errore globale",
    multipleLabel: (count) => `${count} errori globali — espandi per l'elenco completo`,
  })
);

export const globalErrorsExpandedRows = (importData) => (
  messageListExpandedRows(buildCombinedGlobalErrors(importData), {
    headerRows: 4,
  })
);

export const formatImportWarningsDisplay = (warnings) => (
  formatMessageListDisplay(warnings, { title: "Warning mapping" })
);

export const importWarningsCollapsedSummary = (warnings) => (
  messageListCollapsedSummary(warnings, {
    emptyLabel: "Nessun warning",
    multipleLabel: (count) => `${count} warning — espandi per l'elenco completo`,
  })
);

export const importWarningsExpandedRows = (warnings) => (
  messageListExpandedRows(warnings)
);
